import { useState } from 'react';
import { OperationalPoint } from '../store/useStore';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { Info } from 'lucide-react';

const formatUTCTime = (isoStr: string) => {
  if (!isoStr) return '';
  const parts = isoStr.split('T');
  if (parts.length < 2) return '';
  return parts[1].substring(0, 5); // returns HH:mm
};

const formatDateDayMonth = (dateStr: string) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length < 3) return dateStr;
  return `${parts[2]}/${parts[1]}`; // returns DD/MM
};

interface PointInfoBubbleProps {
  point: OperationalPoint;
  onShowDescription?: (point: OperationalPoint) => void;
  isTeamView?: boolean;
}

export function PointInfoBubble({ point, onShowDescription, isTeamView = false }: PointInfoBubbleProps) {
  const { shifts, team, currentUser, categories } = useVisibleStore();
  const [activeDate, setActiveDate] = useState<string | null>(null);

  const category = categories.find(c => c.id === point.categoryId);
  const isArtistScene = category?.isArtistCategory || false;

  const currentUserTeam = currentUser.teamId ? team.find(s => s.id === currentUser.teamId) : null;
  const isCurrentUserArtist = currentUserTeam?.isArtist || false;

  // Hide work schedules for artists viewing non-artist points (they see opening hours only)
  const shouldHideWorkSchedules = !isArtistScene && isCurrentUserArtist;

  // Group opening hours by date — merge into daily open/close range
  const hoursByDate: Record<string, { start: string; end: string; endNextDay?: boolean }[]> = {};

  if (point.openingHours) {
    // Group raw slots by date
    const rawByDate: Record<string, { start: string; end: string; startFull: string; endFull: string }[]> = {};
    point.openingHours.forEach(h => {
      if (!h.start || !h.end) return;
      const date = h.start.split('T')[0];
      if (!rawByDate[date]) rawByDate[date] = [];
      rawByDate[date].push({ start: formatUTCTime(h.start), end: formatUTCTime(h.end), startFull: h.start, endFull: h.end });
    });

    // For each date, compute the overall open→close range
    Object.entries(rawByDate).forEach(([date, slots]) => {
      // Sort by start time
      slots.sort((a, b) => a.startFull.localeCompare(b.startFull));
      const earliest = slots[0];
      const latest = slots.reduce((max, s) => s.endFull > max.endFull ? s : max, slots[0]);

      // Check if the close time is on a later date (overnight)
      const startDate = earliest.startFull.split('T')[0];
      const endDate = latest.endFull.split('T')[0];
      const endNextDay = endDate !== startDate;

      hoursByDate[date] = [{
        start: earliest.start,
        end: latest.end,
        endNextDay,
      }];
    });
  }

  // Get raw shifts at this point
  const rawPointShifts = shifts.filter(s => s.pointId === point.id);

  // Resolve effective team ID(s): for sub-members, also check parent artist shifts
  const effectiveTeamId = currentUser.teamId || currentUserTeam?.id;
  const parentArtistId = currentUserTeam?.parentArtistId;
  const hasShiftAtThisPoint = rawPointShifts.some(s =>
    s.teamId === effectiveTeamId || (parentArtistId && s.teamId === parentArtistId)
  );
  const showInfoButton = !(isTeamView || currentUser.role === 'staff') || hasShiftAtThisPoint;

  // Check if current user is a category supervisor for this point's category
  const myCategoryIds = new Set(currentUserTeam?.categoryIds || (currentUserTeam?.categoryId ? [currentUserTeam.categoryId] : []));
  const isSupervisorForThisPoint = currentUserTeam?.isCategorySupervisor &&
    point.categoryId && myCategoryIds.has(point.categoryId);

  // Apply filtering rules
  let pointShifts = rawPointShifts;
  if (currentUser.role === 'staff') {
    const isPublicCategory = category?.isPublic || false;
    const isStage = point.isStage || false;
    const isPublicOrStage = isPublicCategory || isStage;
    const isRestricted = point.isRestricted || false;
    const isMealPoint = point.isMealPoint || false;

    if (isSupervisorForThisPoint) {
      // Category supervisor: sees ALL shifts in their categories
      pointShifts = rawPointShifts;
    } else if (isPublicOrStage) {
      // Public/stage points: everyone sees the full timetable
      pointShifts = rawPointShifts;
    } else if (isMealPoint) {
      // Meal points: only see own shifts (privacy — no need to see who else is eating)
      pointShifts = rawPointShifts.filter(s =>
        s.teamId === effectiveTeamId || (parentArtistId && s.teamId === parentArtistId)
      );
    } else if (isRestricted) {
      // Restricted: only assigned team see all, others see nothing
      if (hasShiftAtThisPoint) {
        pointShifts = rawPointShifts;
      } else {
        pointShifts = [];
      }
    } else {
      // General non-restricted, non-public points:
      // If assigned → see all shifts (who's before/after you)
      // Otherwise → see nothing
      if (hasShiftAtThisPoint) {
        pointShifts = rawPointShifts;
      } else {
        pointShifts = [];
      }
    }
  }

  const now = new Date();

  // Group assignments by date — merge contiguous shifts per team, hide past shifts
  const teamByDate: Record<string, { shiftId: string; teamId: string; teamName: string; teamRole: string; start: string; end: string; shiftType?: string; startFull: string; endFull: string }[]> = {};

  pointShifts.forEach(shift => {
    if (!shift.start || !shift.end) return;
    // Skip past shifts
    const shiftEnd = new Date(shift.end + (shift.end.endsWith('Z') ? '' : 'Z'));
    if (shiftEnd < now) return;

    const date = shift.start.split('T')[0];
    const assignedTeam = team.find(s => s.id === shift.teamId);
    if (!teamByDate[date]) teamByDate[date] = [];
    teamByDate[date].push({
      shiftId: shift.id,
      teamId: shift.teamId,
      teamName: assignedTeam ? assignedTeam.name : 'Unknown Team',
      teamRole: assignedTeam ? (assignedTeam.roles?.join(', ') || assignedTeam.role || '') : '',
      start: formatUTCTime(shift.start),
      end: formatUTCTime(shift.end),
      shiftType: shift.shiftType,
      startFull: shift.start,
      endFull: shift.end,
    });
  });

  // Sort and merge contiguous shifts per team per date
  Object.keys(teamByDate).forEach(date => {
    // Sort by team name then start time
    teamByDate[date].sort((a, b) => a.teamName.localeCompare(b.teamName) || a.startFull.localeCompare(b.startFull));

    // Merge contiguous shifts for same team
    const merged: typeof teamByDate[string] = [];
    for (const s of teamByDate[date]) {
      const last = merged[merged.length - 1];
      if (last && last.teamName === s.teamName && last.endFull === s.startFull) {
        // Contiguous — merge
        last.end = s.end;
        last.endFull = s.endFull;
        last.shiftId = s.shiftId; // keep latest shift id
      } else {
        merged.push({ ...s });
      }
    }
    teamByDate[date] = merged;
  });

  // Calculate dates to display in tabs — always include opening hours for non-restricted points
  const allDates = Array.from(new Set([...Object.keys(hoursByDate), ...Object.keys(teamByDate)])).sort();

  const currentTab = activeDate || (allDates.length > 0 ? allDates[0] : null);

  return (
    <>
      <div className="text-slate-200 p-4 w-[20rem] max-h-96 overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-lg text-white leading-tight">{point.name}</h3>
          {point.descriptionMarkdown && showInfoButton && (
            <button
              onClick={() => onShowDescription ? onShowDescription(point) : undefined}
              title="Voir la description"
              className="flex-shrink-0 ml-2 flex items-center gap-1 text-[11px] font-bold text-emerald-400 bg-emerald-500/15 hover:bg-emerald-500/30 border border-emerald-500/30 hover:border-emerald-500/60 px-2.5 py-1 rounded-full transition-all"
            >
              <Info className="w-3 h-3" /> Info
            </button>
          )}
        </div>

        {allDates.length > 0 ? (
          <>
            {/* Date tabs */}
            <div className="flex overflow-x-auto space-x-1 border-b border-[#2D3139] pb-1 mb-3 scrollbar-none">
              {allDates.map((date) => (
                <button
                  key={date}
                  onClick={() => setActiveDate(date)}
                  className={`whitespace-nowrap px-2 py-1 text-xs rounded-t-sm transition-colors ${
                    currentTab === date
                      ? 'bg-[#2D3139] text-white font-medium border-b-2 border-indigo-500'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-[#1A1D23]'
                  }`}
                >
                  {formatDateDayMonth(date)}
                </button>
              ))}
            </div>

            {currentTab && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Horaires d'ouverture</h4>
                  {hoursByDate[currentTab] ? (
                    <ul className="space-y-1">
                      {hoursByDate[currentTab].map((h, i) => (
                        <li key={i} className="text-sm bg-[#1A1D23] px-2 py-1 rounded">
                          <span className="text-emerald-400 font-semibold">Ouverture</span> {h.start} → <span className="text-amber-400 font-semibold">Fermeture</span> {h.end}{h.endNextDay ? ' (lendemain)' : ''}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-xs text-slate-500 italic">Non défini</span>
                  )}
                  {shouldHideWorkSchedules && (
                    <p className="text-[10px] text-slate-500 italic mt-2 flex items-center gap-1">
                      ℹ️ Horaires à titre indicatif pour ce point.
                    </p>
                  )}
                </div>

                {!shouldHideWorkSchedules && (
                  <div>
                    <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                      {isArtistScene ? '🎵 Programmation / Timetable' : '🛠️ Horaires de travail'}
                    </h4>
                    {teamByDate[currentTab] && teamByDate[currentTab].length > 0 ? (
                      <div className="space-y-2">
                        <ul className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                          {teamByDate[currentTab].map(s => {
                            const isPerformance = s.shiftType === 'performance';
                            const isSoundcheck = s.shiftType === 'soundcheck';
                            const isMyShift = s.teamId === effectiveTeamId || (parentArtistId && s.teamId === parentArtistId);

                            return (
                              <li
                                key={s.shiftId}
                                className={`p-2.5 rounded-xl border flex flex-col gap-1 transition-all ${
                                  isMyShift
                                    ? 'bg-emerald-500/10 border-emerald-500/40 shadow-[0_0_12px_-3px_rgba(16,185,129,0.2)]'
                                    : isPerformance
                                      ? 'bg-amber-500/[0.04] border-amber-500/30 shadow-[0_0_15px_-3px_rgba(245,158,11,0.05)]'
                                      : isSoundcheck
                                        ? 'bg-slate-800/25 border-slate-700/60'
                                        : 'bg-[#1A1D23] border-[#2D3139]'
                                }`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                    isMyShift
                                      ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
                                      : isPerformance
                                        ? 'bg-amber-500/15 border border-amber-500/30 text-amber-400'
                                        : isSoundcheck
                                          ? 'bg-slate-800 border border-slate-700 text-slate-400'
                                          : 'bg-indigo-500/10 border border-indigo-500/20 text-indigo-400'
                                  }`}>
                                    {isMyShift ? '✅ Mon shift' : isPerformance ? '🎙️ Show' : isSoundcheck ? '🔊 Soundcheck' : '🛠️ Crew'}
                                  </span>

                                  <span className={`font-mono text-xs font-extrabold ${
                                    isMyShift
                                      ? 'text-emerald-400'
                                      : isPerformance
                                        ? 'text-amber-400 animate-gold-blink'
                                        : 'text-slate-300'
                                  }`}>
                                    {s.start} - {s.end}
                                  </span>
                                </div>

                                <div className="flex items-center justify-between mt-1">
                                  <div className={`font-bold text-xs ${isMyShift ? 'text-emerald-400' : 'text-white'}`}>
                                    {s.teamName} {isMyShift && '← Vous'}
                                  </div>
                                  {s.teamRole && (
                                    <span className={`text-[9px] px-2 py-0.5 rounded border ${isMyShift ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' : 'text-slate-400 bg-slate-800/80 border-slate-700/50'}`}>
                                      {s.teamRole}
                                    </span>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                        
                      </div>
                    ) : (
                      <span className="text-xs text-slate-500 italic">Aucun passage programmé</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-400">Aucune information pour ce point.</p>
        )}
      </div>
    </>
  );
}
